import React, { useEffect, useState } from "react";

import { i18n } from "../../translate/i18n";
import ModalImageChatbot from "../ModalImageChatbot";
import useWhatsApps from "../../hooks/useWhatsApps";
import api from "../../services/api";
import { IconSpinner } from "./bulkMessagingUi";

export function TemplatesTable({ whatsappId, bulkMessage, setBulkMessage }) {
	const [templates, setTemplates] = useState([]);
	const [customTemplates, setCustomTemplates] = useState([]);
	const [loading, setLoading] = useState(false);
	const { whatsApps } = useWhatsApps();

	useEffect(() => {
		(async () => {
			if (!whatsappId) return;
			try {
				const { data } = await api.get(`/hub-tamplate?whatsappId=${whatsappId}`);
				setCustomTemplates(data.data || []);
			} catch (error) {
				console.log(error);
			}
		})();
	}, [whatsappId]);

	useEffect(() => {
		if (!whatsappId) return;

		async function getTemplates() {
			try {
				setLoading(true);
				const whatsapp = whatsApps.find((item) => String(item.id) === String(whatsappId));
				if (!whatsapp) return;

				const { wabaAccessToken, wabaAccountId } = whatsapp;
				if (!wabaAccessToken || !wabaAccountId) {
					setTemplates([]);
					return;
				}

				const response = await fetch(
					`https://graph.facebook.com/v15.0/${wabaAccountId}/message_templates`,
					{
						method: "GET",
						headers: {
							Authorization: `Bearer ${wabaAccessToken}`,
							"Content-Type": "application/json",
						},
					}
				);
				const data = await response.json();
				const filteredTemplates = (data.data || []).filter(
					(template) =>
						template.name.includes("notificame") && template.status === "APPROVED"
				);
				setTemplates(filteredTemplates);
			} catch (error) {
				console.log(error);
			} finally {
				setLoading(false);
			}
		}

		getTemplates();
	}, [whatsappId, whatsApps]);

	const handleTemplateSelected = (template) => {
		let templateId;
		let templateText = "";
		let customTemplateId;

		if (!template.status) {
			customTemplateId = template.id;
			templateId = null;
		} else {
			customTemplateId = null;
			templateId = template.id;
		}

		const components = template.components || [];
		const parts = [];

		for (const component of components) {
			const type = String(component.type || "").toUpperCase();
			if (type === "HEADER" && (!component.format || component.format === "TEXT")) {
				parts.push(component.text || "");
			}
			if (type === "BODY") {
				parts.push(component.text || "");
			}
			if (type === "BUTTONS" && Array.isArray(component.buttons)) {
				for (const btn of component.buttons) {
					if (String(btn.type || "").toUpperCase() === "URL") {
						parts.push(btn.url || "");
					}
				}
			}
		}

		templateText = parts.join(" ");
		const templateName =
			template.name ||
			template.elementName ||
			template.title ||
			String(template.id || "");

		setBulkMessage((prev) => ({
			...prev,
			bulkMessagingData: {
				...prev.bulkMessagingData,
				template,
				templateId,
				templateText,
				customTemplateId,
				// Persiste o nome do template para listagem (envio WABA usa templateId).
				message: templateName,
				templateParams: [],
			},
		}));
	};

	const allRows = [...customTemplates, ...templates];
	const selectedId =
		bulkMessage?.bulkMessagingData?.templateId ||
		bulkMessage?.bulkMessagingData?.customTemplateId;

	return (
		<div className="app-table-scroll max-h-[35vh] overflow-auto rounded-md border border-default-200 dark:border-default-700">
			<table className="min-w-full divide-y divide-default-200 dark:divide-default-700">
				<thead className="sticky top-0 bg-default-150 dark:bg-default-800/90">
					<tr className="text-xs font-medium text-default-600 dark:text-default-400">
						<th className="px-3 py-2.5 text-center">Nome</th>
						<th className="px-3 py-2.5 text-center">{i18n.t("templates.table.type")}</th>
						<th className="px-3 py-2.5 text-center">{i18n.t("templates.table.header")}</th>
						<th className="px-3 py-2.5 text-center">{i18n.t("templates.table.body")}</th>
						<th className="px-3 py-2.5 text-center">{i18n.t("templates.table.footer")}</th>
					</tr>
				</thead>
				<tbody className="divide-y divide-default-200 dark:divide-default-700">
					{loading && (
						<tr>
							<td colSpan={5} className="px-3 py-8 text-center">
								<IconSpinner />
							</td>
						</tr>
					)}
					{!loading && allRows.length === 0 && (
						<tr>
							<td colSpan={5} className="px-3 py-6 text-center text-sm text-default-500">
								Nenhum template encontrado!
							</td>
						</tr>
					)}
					{!loading &&
						allRows.map((template) => {
							const rowId = template.id;
							const isSelected = selectedId === rowId;
							const header = template.components?.find((c) => c.type === "HEADER");
							const body = template.components?.find((c) => c.type === "BODY");
							const footer = template.components?.find((c) => c.type === "FOOTER");
							const templateName =
								template.name ||
								template.elementName ||
								template.title ||
								String(template.id || "—");

							return (
								<tr
									key={rowId}
									className={[
										"cursor-pointer text-sm transition-colors",
										isSelected
											? "bg-primary/10 dark:bg-primary/15"
											: "hover:bg-default-100 dark:hover:bg-default-800/50",
									].join(" ")}
									onClick={() => handleTemplateSelected(template)}
								>
									<td
										className="max-w-[12rem] truncate px-3 py-2.5 text-center font-medium"
										title={templateName}
									>
										{templateName}
									</td>
									<td className="px-3 py-2.5 text-center">{template.status || "Custom"}</td>
									<td className="px-3 py-2.5 text-center">
										{header?.format === "IMAGE" ||
										header?.format === "VIDEO" ||
										header?.format === "DOCUMENT" ? (
											<ModalImageChatbot imageUrl={header.example?.header_handle?.[0]} />
										) : (
											header?.text || "—"
										)}
									</td>
									<td className="max-w-xs truncate px-3 py-2.5 text-center">
										{body?.text || "—"}
									</td>
									<td className="px-3 py-2.5 text-center">{footer?.text || "—"}</td>
								</tr>
							);
						})}
				</tbody>
			</table>
		</div>
	);
}
